import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import { FaSearch, FaTimes } from "react-icons/fa";
import { FaCar } from "react-icons/fa6";
import { UserLocation } from "../../TMLocationMap";
import {
  formatDistance,
  getMarkerColorByTime,
} from "../../TMLocationMap.service";

interface ITMListSidebar {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
  userLocations: UserLocation[];
  handleUserClick: (user: UserLocation) => void;
  setHighlightedUser: (id: string | null) => void;
  highlightedUser: string | null;
  currentLocation: any;
}

export const TMListSideBar: React.FC<ITMListSidebar> = ({
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  userLocations,
  handleUserClick,
  setHighlightedUser,
  highlightedUser,
  currentLocation,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<UserLocation[]>([]);

  const formatRelativeTime = (updatedOn?: string): string => {
    if (!updatedOn) return "No timestamp";
    return dayjs(updatedOn).fromNow();
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUsers(userLocations);
    } else {
      const filtered = userLocations.filter((user) =>
        user.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, userLocations]);

  const clearSearch = () => {
    setSearchQuery("");
    setHighlightedUser(null);
  };

  return (
    <div className={`map-sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
      <div className="sidebar-header">
        <h2>Team member list</h2>
        <button
          className="collapse-btn"
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          aria-label={
            isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
          }
        >
          {isSidebarCollapsed ? <AiOutlineRight /> : <AiOutlineLeft />}
        </button>
      </div>

      {!isSidebarCollapsed && (
        <>
          <div className="sidebar-search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by TM ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button className="clear-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
            </div>
            <div className="search-results-count">
              {filteredUsers.length} of {userLocations.length} team members
            </div>
          </div>

          <div className="user-list" style={{ paddingBottom: "100px" }}>
            {filteredUsers.length === 0 ? (
              <div className="empty-state">
                <FaSearch className="empty-icon" />
                <p>No users found</p>
              </div>
            ) : (
              filteredUsers.map((user) => {
                const markerColor = getMarkerColorByTime(user.updatedOn);
                return (
                  <div
                    key={user.id}
                    className={`user-item ${
                      highlightedUser === user.id ? "highlighted" : ""
                    }`}
                    onClick={() => handleUserClick(user)}
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "rgba(0,0,0,.1)",
                    }}
                  >
                    <div className="user-item-header">
                      <div
                        className="user-marker-dot"
                        style={{ backgroundColor: markerColor }}
                      ></div>
                      <span className="user-id">
                        {(user as any).name
                          ? (user as any).name + " (Greeter)"
                          : user.id}
                      </span>
                    </div>
                    <div className="user-item-details">
                      <div
                        className="user-coords"
                        style={{ marginBottom: "-6px" }}
                      >
                        {/* {user.latitude?.toFixed(4)},{" "}
                        {user.longitude?.toFixed(4)} */}
                        <p>Last updated on:</p>
                      </div>
                      {user.updatedOn && (
                        <div className="user-time">
                          {formatRelativeTime(user.updatedOn)}
                        </div>
                      )}
                    </div>

                    {currentLocation && user.distance !== undefined && (
                      <div className="user-distance-info">
                        <div className="distance-item">
                          <span className="distance-icon">📏</span>
                          <span className="distance-text">
                            {formatDistance(user.distance)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};
